import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { FLAVORS, ASSETS, variantLabel } from '../lib/products';
import Crop from '../components/Crop';
import PackImg from '../components/PackImg';
import KrispButton from '../components/KrispButton';

export default function OrderPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => setErr(true));
  }, [orderId]);

  if (err) {
    return (
      <main data-testid="order-not-found" className="min-h-screen flex flex-col items-center justify-center gap-6 px-4">
        <p className="font-display text-4xl stroke-text">ORDER NOT FOUND.</p>
        <Link to="/"><KrispButton testid="order-not-found-home">BACK TO THE DROP →</KrispButton></Link>
      </main>
    );
  }

  if (!order) {
    return <main data-testid="order-loading" className="min-h-screen flex items-center justify-center"><p className="font-display text-2xl animate-pulse">LOADING ORDER...</p></main>;
  }

  return (
    <main data-testid="order-confirmation" className="min-h-screen pt-28 pb-24 px-4 sm:px-8 flex flex-col items-center">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 16 }}
        className="w-24 h-24 border-2 border-kred flex items-center justify-center mt-8"
        style={{ clipPath: 'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)' }}>
        <span className="font-display text-4xl text-kred">✓</span>
      </motion.div>
      <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }}
        className="font-display text-5xl sm:text-7xl text-center mt-8">
        ORDER <span className="text-kred">LOCKED IN.</span>
      </motion.h1>
      <p className="text-xs tracking-[0.3em] text-bone/50 mt-4">ORDER ID: <span className="text-kyellow font-black" data-testid="order-id">#{order.id}</span></p>
      <p className="text-[10px] tracking-[0.2em] text-bone/30 mt-2">PAYMENT: PLACEHOLDER — GATEWAY COMING SOON · STATUS: {order.status}</p>

      <div className="w-full max-w-lg border border-white/10 bg-white/[0.02] p-6 mt-10">
        <p className="text-[10px] tracking-[0.35em] text-bone/40 font-black mb-4">YOUR CRUNCH</p>
        <ul className="space-y-3 mb-5">
          {order.items.map((i) => (
            <li key={`${i.slug}:${i.variant}`} className="flex items-center gap-3" data-testid={`order-item-${i.slug}`}>
              <PackImg f={FLAVORS[i.slug]} variant={i.variant} className="w-10 h-10 border border-white/10 p-0.5" />
              <span className="flex-1 text-xs font-bold">{i.name} · {variantLabel(i.variant)} × {i.qty}</span>
              <span className="text-xs">₹{i.line_total}</span>
            </li>
          ))}
        </ul>
        <div className="border-t border-white/10 pt-4 space-y-1.5 text-xs text-bone/60">
          <div className="flex justify-between"><span>SUBTOTAL</span><span>₹{order.subtotal}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-kyellow"><span>CODE {order.discount_code}</span><span>−₹{order.discount}</span></div>}
          <div className="flex justify-between"><span>SHIPPING</span><span>{order.shipping === 0 ? 'FREE' : `₹${order.shipping}`}</span></div>
          <div className="flex justify-between font-display text-xl text-bone pt-2"><span>TOTAL</span><span data-testid="order-total">₹{order.total}</span></div>
        </div>
        <p className="text-[10px] text-bone/40 mt-5 tracking-[0.1em]">
          SHIPPING TO: {order.customer.name}, {order.customer.address}, {order.customer.city} {order.customer.pincode}
        </p>
      </div>

      <p className="font-marker text-kpink text-xl mt-10">YOU'RE OFFICIALLY ONE OF US NOW.</p>
      <Link to="/" className="mt-8"><KrispButton testid="order-keep-snacking">KEEP SNACKING →</KrispButton></Link>
    </main>
  );
}
